import {
  CrewMerchantApi,
  CrewMerchantUi,
  ReportableEntity,
  helperSchema,
} from 'corso-types';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { columnsByEntity } from '~/utils/dataExport';

export const entityOptions = Object.values(ReportableEntity).map((value) => ({
  value,
  label: value,
}));

type Frequency = NonNullable<
  z.infer<typeof CrewMerchantUi.reportDefinitionCreate>['frequency']
>;

export const frequencyValues = {
  Daily: 'Daily',
  Weekly: 'Weekly',
  Monthly: 'Monthly',
} as const satisfies Record<string, Frequency>;

export const frequencyOptions = Object.values(frequencyValues).map((value) => ({
  value,
  label: value,
}));

export const exportWeeklyOptions = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

export const exportMonthlyOptions = Array.from({ length: 28 }, (_, i) => ({
  value: `${i + 1}`,
  label: `${i + 1}`,
}));

export const formatSchedule = ({
  frequency,
  exportDay,
  exportEmails,
}: {
  frequency?: Frequency | null;
  exportDay?: number | null;
  exportEmails?: string | null;
}) => {
  if (!frequency) return undefined;

  const dayPhrase = match(frequency)
    .with('Daily', () => null)
    .with('Weekly', () => {
      if (exportDay == null) return null;
      return (
        exportWeeklyOptions.find((o) => o.value === `${exportDay}`)?.label ??
        null
      );
    })
    .with('Monthly', () => (exportDay == null ? null : `day ${exportDay}`))
    .exhaustive();

  const base = dayPhrase ? `${frequency} on ${dayPhrase}` : frequency;
  return exportEmails ? `${base}, emailed to ${exportEmails}` : base;
};

export const reportDefinitionFormSchema = z
  .object({
    kind: z.enum(Object.values(ReportableEntity)),
    name: z.string(),
    filters: z.record(z.string(), z.unknown()),
    jsonDefinition: z.object({
      columns: z.array(z.string()).min(1),
      lineItemCustomFieldIds: z.array(z.number()).optional(),
      inspectionCustomFieldIds: z.array(z.number()).optional(),
      registrationCustomFieldIds: z.array(z.number()).optional(),
    }),
    frequency: z.string().optional(),
    exportDay: z.number().optional(),
    exportEmails: helperSchema.emailList.optional(),
  })
  // Scheduled runs have no logged-in user to fall back to, so without at
  // least one recipient the cron stages a row that immediately Fails for
  // having no one to notify. Block submit until the merchant fills in emails.
  .refine(
    ({ frequency, exportEmails }) =>
      !frequency ||
      (typeof exportEmails === 'string' && exportEmails.length > 0),
    {
      message:
        'Add at least one notification email when a schedule is enabled.',
      path: ['exportEmails'],
    },
  );

export const buildDefaults = (entity: ReportableEntity) =>
  match(entity)
    .returnType<z.infer<typeof reportDefinitionFormSchema>>()
    .with('Return', (kind) => ({
      kind,
      name: '',
      filters: {},
      exportDay: undefined,
      exportEmails: '',
      frequency: undefined,
      jsonDefinition: {
        columns: Object.values(columnsByEntity.Return),
        lineItemCustomFieldIds: [],
        inspectionCustomFieldIds: [],
      },
    }))
    .with('Warranty', (kind) => ({
      kind,
      name: '',
      exportDay: undefined,
      exportEmails: '',
      frequency: undefined,
      filters: {},
      jsonDefinition: {
        columns: Object.values(columnsByEntity.Warranty),
        lineItemCustomFieldIds: [],
        inspectionCustomFieldIds: [],
      },
    }))
    .with('Registration', (kind) => ({
      kind,
      name: '',
      exportDay: undefined,
      exportEmails: '',
      frequency: undefined,
      filters: {},
      jsonDefinition: {
        columns: Object.values(columnsByEntity.Registration),
        registrationCustomFieldIds: [],
      },
    }))
    .with('Shipping', (kind) => ({
      kind,
      name: '',
      exportDay: undefined,
      exportEmails: '',
      frequency: undefined,
      filters: {},
      jsonDefinition: {
        columns: Object.values(columnsByEntity.Shipping),
      },
    }))
    .exhaustive();

export const definitionToFormValues = ({
  kind,
  name,
  filters,
  jsonDefinition,
  frequency,
  exportDay,
  exportEmails,
}: CrewMerchantApi.ResponseBody<
  '/:storeId/reports/:entity/definitions/:definitionId',
  'get'
>) => ({
  kind,
  name,
  filters,
  jsonDefinition,
  frequency,
  exportDay,
  exportEmails,
});
